import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 120000,
  withCredentials: true,
});

// ──────────── In-flight request tracking (for GlobalProgressBar) ────────────
let _inflight = 0;
const _subs = new Set();
function _emit() { _subs.forEach(fn => { try { fn(_inflight); } catch {} }); }

export function subscribeInflight(cb) {
  _subs.add(cb);
  cb(_inflight);
  return () => _subs.delete(cb);
}

api.interceptors.request.use((config) => {
  _inflight += 1; _emit();
  return config;
});
api.interceptors.response.use(
  (res) => { _inflight = Math.max(0, _inflight - 1); _emit(); return res; },
  (err) => { _inflight = Math.max(0, _inflight - 1); _emit(); return Promise.reject(err); }
);

// ──────────── E2 — client GET cache: dedupe identical in-flight GETs + 30s reuse ────────────
// Any non-GET request clears the cache (writes must never show stale data). Polling/status endpoints bypass.
const CLIENT_TTL_MS = 30000;
const NO_CACHE = /status|progress|\/jobs|backfill|perf-report|\/auth\/|health-check|reply-status|\/pms\/|export|download|csv/;
const _getCache = new Map();   // key -> { t, res }
const _getInflight = new Map(); // key -> promise

const _keyOf = (url, config) => `${url}?${JSON.stringify(config?.params || {})}`;
const _rawGet = api.get.bind(api);
api.get = (url, config = {}) => {
  if (NO_CACHE.test(url) || config.noCache) return _rawGet(url, config);
  const key = _keyOf(url, config);
  const hit = _getCache.get(key);
  if (hit && Date.now() - hit.t < CLIENT_TTL_MS) return Promise.resolve(hit.res);
  if (_getInflight.has(key)) return _getInflight.get(key);
  const p = _rawGet(url, config)
    .then((res) => { _getCache.set(key, { t: Date.now(), res }); return res; })
    .finally(() => _getInflight.delete(key));
  _getInflight.set(key, p);
  return p;
};
export function clearApiCache() { _getCache.clear(); }
["post", "put", "patch", "delete"].forEach((m) => {
  const raw = api[m].bind(api);
  api[m] = (...args) => raw(...args).finally(() => {
    if (!(m === "post" && /\/meta\/insights$/.test(args[0]))) clearApiCache();
  });
});

export function errorMessage(e, fallback = "發生未知錯誤") {
  const d = e?.response?.data;
  if (d?.error?.message) return d.error.message;
  if (d?.detail?.error?.message) return d.detail.error.message;
  if (typeof d?.detail === "string") return d.detail;
  return e?.message || fallback;
}

export function formatInt(n) {
  if (n === null || n === undefined || n === "") return "—";
  const num = typeof n === "string" ? parseFloat(n) : n;
  if (Number.isNaN(num)) return "—";
  return Math.round(num).toLocaleString("en-US");
}

export function formatMoney(n, currency = "") {
  if (n === null || n === undefined || n === "") return "—";
  const num = typeof n === "string" ? parseFloat(n) : n;
  if (Number.isNaN(num)) return "—";
  const s = Math.round(num).toLocaleString("en-US");
  return currency ? `${currency} ${s}` : s;
}

export function formatPercent(n) {
  if (n === null || n === undefined || n === "") return "—";
  const num = typeof n === "string" ? parseFloat(n) : n;
  if (Number.isNaN(num)) return "—";
  return `${num.toFixed(2)}%`;
}

export function formatTs(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("zh-TW", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}
